/**
 * Prepare uploaded images for visual search (resize + optional center crop).
 */

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image'));
    img.src = src;
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read image file'));
    reader.readAsDataURL(file);
  });
}

/**
 * @param {File} file
 * @param {{ crop?: 'center' | 'none', maxSize?: number, quality?: number }} options
 */
export async function prepareImageForSearch(file, options = {}) {
  const crop = options.crop || 'center';
  const maxSize = options.maxSize || 1280;
  const quality = options.quality || 0.92;
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImage(dataUrl);

  let sx = 0;
  let sy = 0;
  let sw = img.width;
  let sh = img.height;

  if (crop === 'center') {
    const size = Math.min(img.width, img.height);
    sx = (img.width - size) / 2;
    sy = (img.height - size) / 2;
    sw = size;
    sh = size;
  }

  const scale = Math.min(1, maxSize / Math.max(sw, sh));
  const outW = Math.round(sw * scale);
  const outH = Math.round(sh * scale);

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) throw new Error('Could not process image');

  const baseName = (file.name || 'search').replace(/\.[^.]+$/, '');
  const outFile = new File([blob], `${baseName}-search.jpg`, { type: 'image/jpeg' });
  const preview = canvas.toDataURL('image/jpeg', quality);

  return { file: outFile, dataUrl: preview };
}

export default { prepareImageForSearch };
